import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ProductQuoteProductType, ProductQuoteRequestItem, ProductQuoteResult } from '@typesApp/ProductQuote'
import { useLazyGetProductQuoteQuery } from '@redux/queries/claimRecords'
import { claimRecordIdSelector } from '@redux/slices/claimStatusSlice'
import { setProductQuoteError } from '@redux/slices/insuranceMemberSlice'
import { isFetchBaseError, isGatewayTimeout } from '@utils/errorUtil'
import { CommerceProductQuoteRequestItem } from '../types'

const FRAME_PRODUCT_TYPES: ProductQuoteProductType[] = [
  'FRAME',
  'FRAME_ONLY',
  'RIMLESS_FRAME',
  'GLASSES',
  'SUNGLASSES',
  'PRESCRIPTION_SUNGLASSES',
]

const CONTACTS_PRODUCT_TYPES: ProductQuoteProductType[] = [
  'COLORED_PLANO_CONTACT_LENS',
  'PRESCRIPTION_CONTACT_LENS',
  'MEDICAL_CONDITION_CONTACT_LENS',
]

type UseProductQuoteReturn = {
  isLoading: boolean
  isError: boolean
  requestQuote: (request: CommerceProductQuoteRequestItem[]) => Promise<ProductQuoteResult>
}

export const useProductQuoteService = (): UseProductQuoteReturn => {
  const dispatch = useDispatch()
  const claimRecordId = useSelector(claimRecordIdSelector)
  const [triggerRequest, { isLoading, isError }] = useLazyGetProductQuoteQuery()

  const mapCommerceProducts = (products: CommerceProductQuoteRequestItem[]): ProductQuoteRequestItem[] => {
    return (products ?? []).map(product => {
      const {
        productType,
        quantity = 1,
        unitFullPrice,
        serviceCategories,
        productIdentifier,
        productName,
        eye,
        groupId,
      } = product

      const isFrame = FRAME_PRODUCT_TYPES.includes(productType)
      const isContacts = CONTACTS_PRODUCT_TYPES.includes(productType)

      const result: ProductQuoteRequestItem = {
        productType,
        productName,
        ecommerceId: productIdentifier,
        ecommerceIdType: isFrame || isContacts ? 'UPC' : 'RXC_ID',
        quantity,
        unitFullPrice,
        eye,
        groupId,
        serviceCategories: Array.isArray(serviceCategories) && serviceCategories.length ? serviceCategories : undefined,
      }

      if (!isContacts) {
        delete result.eye
        delete result.groupId

        if (!isFrame) {
          delete result.productName
        }
      }

      return result
    })
  }

  const requestQuote = useCallback(
    async (request: CommerceProductQuoteRequestItem[]): Promise<ProductQuoteResult> => {
      if (!claimRecordId) {
        return { productQuotes: [] }
      }

      dispatch(setProductQuoteError(false))
      const products = mapCommerceProducts(request)

      const requestWithRetry = async (): Promise<ProductQuoteResult> => {
        try {
          return await triggerRequest({ claimRecordId, products }).unwrap()
        } catch (error) {
          if (isFetchBaseError(error) && isGatewayTimeout(error.status)) {
            return await triggerRequest({ claimRecordId, products }).unwrap()
          } else {
            throw error
          }
        }
      }

      try {
        return await requestWithRetry()
      } catch (error) {
        dispatch(setProductQuoteError(true))
        return { productQuotes: [], isError: true }
      }
    },
    [dispatch, triggerRequest, claimRecordId]
  )

  return {
    isLoading,
    isError,
    requestQuote,
  }
}
